/**
 * Which protocol serves a parcel's remote: the Module Federation 2 runtime
 * (primary) or the classic `window[scope]` container (the transition fallback,
 * retired in the MF2 endgame).
 *
 * With an entry, the runtime serves it when a host instance exists or the entry
 * is a `.json` manifest, which a standalone instance can load and the classic
 * `<script>` registration cannot. Without an entry, the runtime can only serve a
 * remote the host knows from its own bootstrap: `loadRemote` on an unknown name
 * throws (RUNTIME-004) instead of falling back, and a loader-own registration
 * does not count, or a disabled override would keep serving the stale entry.
 *
 * A container the classic protocol already installed, which no runtime instance
 * registered, stays on that protocol even when an entry resolves. The runtime
 * would find the same global and call `init` with its own share scope, which an
 * initialised webpack container refuses, so every retry would repeat the failure.
 */
export interface LoadStrategyInput {
  /** The resolved remote entry, when one resolves. */
  entry: string | undefined;
  /** A host runtime instance exists. */
  hasHost: boolean;
  /** `entry` is a `.json` manifest. */
  manifestEntry: boolean;
  /** A container already sits on `window[scope]`. */
  classicContainer: boolean;
  /** A loader registered the scope on some runtime instance, host or standalone. */
  runtimeRegistered: boolean;
  /** The host instance knows the remote, from its bootstrap or from a loader. */
  hostKnowsRemote: boolean;
  /** A loader, not the host bootstrap, registered the scope on the host instance. */
  loaderRegisteredOnHost: boolean;
}

export type LoadStrategy = 'runtime' | 'classic';

export function resolveLoadStrategy(input: LoadStrategyInput): LoadStrategy {
  const foreignClassicContainer =
    input.classicContainer && !input.runtimeRegistered && !input.hostKnowsRemote;
  if (input.entry) {
    return !foreignClassicContainer && (input.hasHost || input.manifestEntry)
      ? 'runtime'
      : 'classic';
  }
  return input.hasHost && input.hostKnowsRemote && !input.loaderRegisteredOnHost
    ? 'runtime'
    : 'classic';
}
